/**
 * <s-product-gallery> — main product image track with thumbnail navigation.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontally scrolling, snap-aligned slide track.
 *   [data-gallery-slide]          — one slide per media item, in order.
 *   button[data-gallery-thumb]    — thumbnail buttons; data-index matches the slide index.
 *
 * Thumbnails scroll the track to their slide; the active thumbnail follows the
 * slide in view so swipes and thumbnail clicks stay in sync.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#handleThumbClick, opts);

    const track = this.querySelector('[data-gallery-track]');
    const slides = this.querySelectorAll('[data-gallery-slide]');
    if (!track || slides.length < 2 || !('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = Array.prototype.indexOf.call(slides, entry.target);
          if (index > -1) this.#setActive(index);
        }
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleThumbClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('button[data-gallery-thumb]');
    if (!thumb || !this.contains(thumb)) return;

    const index = Number.parseInt(thumb.dataset.index || '0', 10);
    const track = this.querySelector('[data-gallery-track]');
    const slide = this.querySelectorAll('[data-gallery-slide]')[index];
    if (!track || !(slide instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track horizontally. The track is position: relative, so offsetLeft is track-relative.
    const left = slide.offsetLeft;
    track.scrollTo({ left: getComputedStyle(track).direction === 'rtl' ? -Math.abs(track.scrollWidth - track.clientWidth - left) : left, behavior: reduceMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  };

  #setActive(index) {
    this.querySelectorAll('button[data-gallery-thumb]').forEach((thumb) => {
      const active = Number.parseInt(thumb.dataset.index || '-1', 10) === index;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
